import React, { useState, useEffect, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faCalendarAlt, faVenusMars, faEnvelope, faPhone,
  faMapMarkerAlt, faGraduationCap, faBriefcase, faLanguage,
  faIdCard, faFilePdf, faCheckCircle, faExclamationTriangle,
  faStar, faEye, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { CandidatProfilContext } from '../layout/CunstomCandidatProfil'; // ajustez le chemin

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('ProfilCandidatCache');
db.version(1).stores({ profil: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedProfil(key, allowExpired = false) {
  try {
    const entry = await db.profil.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedProfil(key, data) {
  await db.profil.put({ key, data, timestamp: Date.now() });
}
async function getStaleProfil(key) {
  return getCachedProfil(key, true);
}

const API_PROFIL = `${API_CONFIG2.apiUrl}candidat/profil.php`;
const API_CV = `${API_CONFIG2.apiUrl}candidat/cv.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ProfilCandidat = ({ candidatId }) => {
  // Contexte
  const context = useContext(CandidatProfilContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [candidat, setCandidat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  // ============================================================
  // CHARGEMENT DU PROFIL (avec cache, 429, contexte)
  // ============================================================
  const fetchProfil = async (forceRefresh = false, retryCount = 0) => {
    if (!candidatId) {
      setLoading(false);
      setError("Aucun identifiant de candidat fourni.");
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cacheKey = `profil_${candidatId}`;

      // 1. Cache si non forcé
      if (!forceRefresh) {
        const cached = await getCachedProfil(cacheKey);
        if (cached) {
          setCandidat(cached);
          setDataSource('cache');
          if (setSource) setSource('cache');
          setLoading(false);
          return;
        }
      }

      // 2. Requête réseau
      const url = `${API_PROFIL}?id=${candidatId}`;
      const response = await fetch(url);

      // Gestion 429
      if (response.status === 429) {
        const stale = await getStaleProfil(cacheKey);
        if (stale) {
          setCandidat(stale);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          setToast({ show: true, message: '429 – Utilisation du cache (peut être obsolète)', variant: 'warning' });
          setTimeout(() => setToast({ show: false }), 5000);
          setLoading(false);
          return;
        }
        // Sinon, réessayer avec backoff
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        setToast({ show: true, message: `Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, variant: 'warning' });
        setTimeout(() => setToast({ show: false }), 5000);
        setTimeout(() => fetchProfil(forceRefresh, retryCount + 1), waitTime);
        return;
      }

      if (!response.ok) {
        // Fallback cache périmé en cas d'erreur réseau
        const stale = await getStaleProfil(cacheKey);
        if (stale) {
          setCandidat(stale);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          setToast({ show: true, message: `Erreur réseau – Utilisation du cache`, variant: 'warning' });
          setTimeout(() => setToast({ show: false }), 5000);
          setLoading(false);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // Lecture du JSON
      const data = await response.json();
      if (data.status === 'success') {
        setCandidat(data.data);
        const source = forceRefresh ? 'live (forcé)' : 'live';
        setDataSource(source);
        if (setSource) setSource(source);
        await setCachedProfil(cacheKey, data.data);
        if (forceRefresh) {
          setToast({ show: true, message: 'Profil actualisé', variant: 'success' });
          setTimeout(() => setToast({ show: false }), 3000);
        }
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('fetchProfil error:', err);
      // Fallback cache périmé
      const cacheKey = `profil_${candidatId}`;
      const stale = await getStaleProfil(cacheKey);
      if (stale) {
        setCandidat(stale);
        setDataSource('cache (périmé)');
        if (setSource) setSource('cache (périmé)');
        setToast({ show: true, message: `Erreur – Utilisation du cache`, variant: 'warning' });
        setTimeout(() => setToast({ show: false }), 5000);
      } else {
        setError(err.message);
        setToast({ show: true, message: err.message, variant: 'danger' });
        setTimeout(() => setToast({ show: false }), 5000);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchProfil(true);
    }
  }, [refreshKey]);

  // ============================================================
  // CHARGEMENT INITIAL
  // ============================================================
  useEffect(() => {
    fetchProfil();
  }, [candidatId]);

  // ============================================================
  // OUVERTURE DU CV
  // ============================================================
  const openCV = () => {
    if (candidat?.cvUrl) {
      window.open(`${API_CV}?id=${candidatId}`, '_blank');
    } else {
      setToast({ show: true, message: 'Ce candidat n\'a pas de CV', variant: 'warning' });
      setTimeout(() => setToast({ show: false }), 3000);
    }
  };

  // ============================================================
  // AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary" />
        <p className="text-secondary mt-2">Chargement du profil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-3" />
        <p>{error}</p>
        <button className="monetra-btn primary" onClick={() => fetchProfil(true)}>
          Réessayer
        </button>
      </div>
    );
  }

  if (!candidat) return null;

  // ============================================================
  // RENDU
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – PROFIL CANDIDAT (amélioré)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .profil-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 0;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 2rem;
          box-shadow: var(--shadow);
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 16px 48px rgba(59, 130, 246, 0.08);
          transform: translateY(-2px);
        }

        .profil-header {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        }

        .avatar-large {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.8rem;
          font-weight: 700;
          color: #fff;
          box-shadow: 0 0 40px rgba(59, 130, 246, 0.25);
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }

        .avatar-large:hover {
          transform: scale(1.05);
        }

        .profil-title h2 {
          font-weight: 700;
          margin: 0;
          font-size: 2.2rem;
          background: linear-gradient(135deg, #e0e7ff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .profil-title .badge-id {
          color: var(--text-muted);
          font-size: 0.85rem;
        }

        .statut-badge {
          display: inline-block;
          padding: 4px 18px;
          border-radius: 30px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .statut-badge.accepte {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }
        .statut-badge.refusee {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
        }
        .statut-badge.en-attente {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }
        .statut-badge.inactif {
          background: rgba(255, 255, 255, 0.04);
          color: var(--text-secondary);
        }

        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 14px;
          border: 1px solid rgba(255, 255, 255, 0.04);
          transition: all 0.25s ease;
        }

        .info-item:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(59, 130, 246, 0.15);
          transform: translateX(4px);
        }

        .info-item .info-icon {
          width: 28px;
          color: var(--accent-blue);
          opacity: 0.7;
          font-size: 1.1rem;
        }

        .info-item .info-label {
          font-size: 0.7rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 2px;
        }

        .info-item .info-value {
          font-weight: 500;
          font-size: 0.95rem;
        }

        .btn-cv {
          padding: 10px 24px;
          border-radius: 30px;
          background: rgba(59, 130, 246, 0.10);
          border: 1px solid rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          transition: all 0.25s ease;
          cursor: pointer;
          text-decoration: none;
        }

        .btn-cv:hover {
          background: rgba(59, 130, 246, 0.20);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.15);
        }

        .monetra-btn {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .monetra-btn:hover {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }

        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }

        .monetra-btn.primary:hover {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }

        .cv-card {
          margin-top: 1.5rem;
          padding: 1rem 1.5rem;
          background: rgba(59, 130, 246, 0.04);
          border-radius: 14px;
          border: 1px solid rgba(59, 130, 246, 0.08);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
        }

        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }

        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
        }

        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }

        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }

        .btn-close-custom:hover {
          color: var(--text-primary);
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-danger { color: #ef4444; }
        .mt-3 { margin-top: 1rem; }
        .me-2 { margin-right: 0.5rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .d-flex { display: flex; }
        .align-items-center { align-items: center; }
        .gap-3 { gap: 1rem; }
        .flex-wrap { flex-wrap: wrap; }

        @media (max-width: 768px) {
          .profil-container { padding: 0; }
          .glass-card { padding: 1.2rem; }
          .info-grid { grid-template-columns: 1fr; }
          .avatar-large { width: 72px; height: 72px; font-size: 2rem; }
          .profil-title h2 { font-size: 1.6rem; }
          .cv-card { flex-direction: column; align-items: stretch; }
        }
      `}</style>

      <div className="profil-container">
        <div className="glass-card">
          {/* En-tête */}
          <div className="profil-header">
            <div className="avatar-large">
              {candidat.prenom?.charAt(0)}{candidat.nom?.charAt(0)}
            </div>
            <div className="profil-title">
              <h2>{candidat.prenom} {candidat.nom}</h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className={`statut-badge ${candidat.statut?.toLowerCase().replace(' ', '-') || 'inactif'}`}>
                  {candidat.statut || 'Non défini'}
                </span>
                <span className="badge-id">ID #{candidat.id}</span>
                <button className="btn-cv" onClick={openCV}>
                  <FontAwesomeIcon icon={faFilePdf} /> Voir le CV
                </button>
              </div>
            </div>
          </div>

          {/* Grille d'informations */}
          <div className="info-grid">
            <div className="info-item">
              <FontAwesomeIcon icon={faCalendarAlt} className="info-icon" />
              <div>
                <div className="info-label">Date de naissance</div>
                <div className="info-value">{candidat.date_naissance || '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <FontAwesomeIcon icon={faVenusMars} className="info-icon" />
              <div>
                <div className="info-label">Sexe</div>
                <div className="info-value">{candidat.sexe || '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <FontAwesomeIcon icon={faEnvelope} className="info-icon" />
              <div>
                <div className="info-label">Email</div>
                <div className="info-value">{candidat.email || '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <FontAwesomeIcon icon={faPhone} className="info-icon" />
              <div>
                <div className="info-label">Téléphone</div>
                <div className="info-value">{candidat.tel || '—'}</div>
              </div>
            </div>
            <div className="info-item" style={{ gridColumn: 'span 2' }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="info-icon" />
              <div>
                <div className="info-label">Adresse</div>
                <div className="info-value">{candidat.adresse || '—'}</div>
              </div>
            </div>
          </div>

          {/* Deuxième grille */}
          <div className="info-grid" style={{ marginTop: '0.5rem' }}>
            <div className="info-item">
              <FontAwesomeIcon icon={faGraduationCap} className="info-icon" />
              <div>
                <div className="info-label">Niveau d'étude</div>
                <div className="info-value">{candidat.niveau_etude || '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <FontAwesomeIcon icon={faBriefcase} className="info-icon" />
              <div>
                <div className="info-label">Expérience</div>
                <div className="info-value">{candidat.experience || '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <FontAwesomeIcon icon={faLanguage} className="info-icon" />
              <div>
                <div className="info-label">Langues</div>
                <div className="info-value">{candidat.langues || '—'}</div>
              </div>
            </div>
            <div className="info-item" style={{ gridColumn: 'span 2' }}>
              <FontAwesomeIcon icon={faStar} className="info-icon" />
              <div>
                <div className="info-label">Compétences</div>
                <div className="info-value">{candidat.competence || '—'}</div>
              </div>
            </div>
          </div>

          {/* CV */}
          {candidat.cvUrl && (
            <div className="cv-card">
              <span className="text-secondary small">
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                CV : {candidat.cvUrl.split('/').pop()}
              </span>
              <button className="btn-cv" onClick={openCV} style={{ padding: '6px 20px' }}>
                <FontAwesomeIcon icon={faEye} /> Ouvrir
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilCandidat;